import React, { FC, useContext, useState } from 'react';
import { ButtonSize } from 'enums/ButtonEnums';
import { Formik, Form, FormikState } from 'formik';
import COButton from 'shared/components/COButton';
import {
  baselineYearList,
  categoryList,
} from 'views/CompanySetup/companySetupConstants';
import { fetchGeocodeData } from 'shared/utils/fetchGeoLocation';
import fetchAddressFromGeoCode from 'shared/utils/fetchAddressFromGeoCode';
import { validationSchema } from './editLocationValidationSchema';
import { Location } from 'models/ActivityDataEntryLocation/location.model';
import { OrganizationMetaData } from 'models/OrganizationMetaData.ts';
import { useParams } from 'react-router-dom';
import { Address } from 'models/Address/address.model';
import OrganizationBoundaryModal from './OrganizationBoundaryModal';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { HelpDeskInputFieldEnum } from 'context/HelpDeskTooltipContext/helpDeskInputFieldEnum';
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import LocationDetailsForm from './LocationDetailsForm';
import styles from './editLocation.module.scss';

interface EditLocationFormProps {
  isLocationEdit: boolean;
  setIsLocationEdit?: React.Dispatch<React.SetStateAction<boolean>>;
  locationDetails?: Location;
  orgListMeta?: OrganizationMetaData[];
  updateLocation?: (
    locationId?: number | undefined,
    payload?: Location | undefined
  ) => Promise<void>;
  fetchLocation?: (locationId?: number | undefined) => Promise<void>;
  handleEdit?: () => void;
}

const EditLocationForm: FC<EditLocationFormProps> = ({
  setIsLocationEdit,
  locationDetails,
  orgListMeta,
  updateLocation,
  fetchLocation,
  handleEdit,
  isLocationEdit,
}) => {
  const [isEdiBoundaryModalOpen, setIsEditBoundaryModalOpen] =
    useState<boolean>(false);
  const handleCancelClick =
    (resetForm: (nextState?: Partial<FormikState<Location>>) => void) => () => {
      resetForm();
      setIsLocationEdit?.(false);
    };
  const { id: locationId } = useParams();
  const { openTooltipModal } = useContext(HelpDeskTooltipContext);

  const searchPlaceByCountryProvince =
    (country?: string, province?: string, setFieldValue?: Function) =>
    async () => {
      try {
        const results = await fetchGeocodeData(`${province} ${country}`);
        if (results) {
          setFieldValue?.('locationMap', results?.formattedAddress);
          setAddressInState(setFieldValue)(
            results?.addressComponents,
            results?.latLng
          );
        }
      } catch (error) {
      } finally {
      }
    };

  const handleModalOpen = () => setIsEditBoundaryModalOpen(true);
  const handleModalClose = () => setIsEditBoundaryModalOpen(false);

  const desiredInitialValues = baselineYearList.map(year => ({
    id: undefined,
    year: year.value,
    sharePercentage: '',
    isActive: false,
  }));

  const initialValuesForBoundaryDetails = desiredInitialValues.map(yearItem => {
    const matchedItem = locationDetails?.yearsWithScopeStatus?.find(
      yearValue => yearValue.year === yearItem.year
    );

    if (matchedItem) {
      return {
        ...yearItem,
        id: matchedItem.id,
        sharePercentage: matchedItem.sharePercentage ?? '',
        isActive: matchedItem.isActive,
      };
    }
    return yearItem;
  });

  const initialValues: Location = {
    ...locationDetails,
    yearsWithScopeStatus: initialValuesForBoundaryDetails ?? [],
  };

  const setAddressInState =
    (setFieldValue?: Function) =>
    (
      addressComponents: google.maps.GeocoderAddressComponent[],
      latlng: google.maps.LatLng | null
    ) => {
      const { streetLocality, country, province, city, pinCode } =
        fetchAddressFromGeoCode(addressComponents);

      setFieldValue?.(`location.street`, streetLocality?.trim() ?? '');
      setFieldValue?.(`location.country`, country?.long_name ?? '');
      setFieldValue?.(`location.countryCode`, country?.short_name ?? '');
      setFieldValue?.(`location.province`, province?.long_name ?? '');
      setFieldValue?.(`location.provinceCode`, province?.short_name ?? '');
      setFieldValue?.(`location.city`, city?.long_name ?? '');
      setFieldValue?.(`location.postalCode`, pinCode?.long_name ?? '');
      setFieldValue?.(`location.latitude`, latlng?.lat() ?? '');
      setFieldValue?.(`location.longitude`, latlng?.lng() ?? '');
    };
  const handleSubmit = async (values: Location) => {
    const formattedValues = {
      ...values,
      yearsWithScopeStatus: values?.yearsWithScopeStatus
        ?.filter(
          item => item?.sharePercentage !== '' || item?.isActive || item?.id
        )
        .map(item => ({
          id: item?.id,
          year: item?.year,
          sharePercentage: Number(item?.sharePercentage),
          isActive: item?.isActive,
        })),
    };
    await updateLocation?.(Number(locationId), formattedValues);
    fetchLocation?.(Number(locationId));
  };
  const { getFormattedAddress } = locationDetails?.location ?? new Address();
  const locationFullAddresss = getFormattedAddress?.();

  const isCompleted = locationDetails?.yearsWithScopeStatus?.some(
    item => item?.isActive && item?.sharePercentage !== null
  );

  const handleToolTipOpen = (fieldName?: HelpDeskInputFieldEnum) => () =>
    fieldName && openTooltipModal(fieldName);

  return (
    <div className={styles.editLocationDetailsContainer}>
      <div className={styles.formContainer}>
        <div className={styles.headerContainer}>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}>
            {({ setFieldValue, values, resetForm }) => (
              <Form>
                {isLocationEdit ? (
                  <div className={styles.actionContainer}>
                    <div>
                      <COButton
                        text
                        size={ButtonSize.SMALL}
                        onClick={handleCancelClick(resetForm)}>
                        Cancel
                      </COButton>
                    </div>
                    <div>
                      <COButton primary size={ButtonSize.SMALL} submit>
                        Save
                      </COButton>
                    </div>
                  </div>
                ) : (
                  <div className={styles.buttonContainer}>
                    <div>
                      <COButton
                        text
                        icon={<EditIcon />}
                        onClick={handleEdit}
                        size={ButtonSize.SMALL}>
                        Edit
                      </COButton>
                    </div>
                  </div>
                )}
                <LocationDetailsForm
                  isLocationEdit={isLocationEdit}
                  setFieldValue={setFieldValue}
                  values={values}
                  locationFullAddress={locationFullAddresss}
                  categoryList={categoryList}
                  orgListMeta={orgListMeta}
                  isCompleted={isCompleted}
                  handleToolTipOpen={handleToolTipOpen}
                  handleModalOpen={handleModalOpen}
                  searchPlaceByCountryProvince={searchPlaceByCountryProvince}
                  setAddressInState={setAddressInState}
                />
                <OrganizationBoundaryModal
                  isEdiBoundaryModalOpen={isEdiBoundaryModalOpen}
                  handleModalClose={handleModalClose}
                  setIsEditBoundaryModalOpen={setIsEditBoundaryModalOpen}
                  yearsWithScopeStatus={locationDetails?.yearsWithScopeStatus}
                  isLocationEdit={isLocationEdit}
                />
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default EditLocationForm;
